import axios from 'axios';
import type { DuplicateSuspectedPayload, OperationRequest } from '../api/types';

/**
 * Client mirror of the backend's `duplicate-key.ts` — the ONE answer to «is this
 * the same money?». Kept in sync by hand, exactly like `lib/operationShape.ts`
 * mirrors the server's shape table.
 *
 * `comment`, author and submit time are excluded on purpose: a seller who
 * re-types an entry with a different comment is the case we are hunting.
 * `deductFromStoreBalance` is included: it changes the balance effect.
 */
export function requestDuplicateKey(req: OperationRequest): string {
  return [
    req.storeId,
    req.type,
    req.date,
    req.amount,
    req.categoryId ?? '-',
    req.paymentMethod ?? '-',
    req.deductFromStoreBalance ? 1 : 0,
  ].join('|');
}

/**
 * Look-alike groups within one list: request id → a shared group id. Only keys
 * carried by two or more rows produce an entry, so a singleton is never flagged.
 *
 * This is the client's own safety net for lists the server has not annotated
 * (and it works on an older API build). It can only see twins that are BOTH in
 * the list — a twin already approved into the ledger is invisible here, which is
 * why the server also sends `duplicateOperationId`.
 */
export function clientDuplicateGroups(requests: OperationRequest[]): Map<string, string> {
  const byKey = new Map<string, string[]>();
  for (const req of requests) {
    const key = requestDuplicateKey(req);
    const ids = byKey.get(key);
    if (ids) ids.push(req.id);
    else byKey.set(key, [req.id]);
  }
  const groups = new Map<string, string>();
  for (const [key, ids] of byKey) {
    if (ids.length < 2) continue;
    for (const id of ids) groups.set(id, key);
  }
  return groups;
}

/** Does this row look like a repeat — by the server's verdict or the client's? */
export function isLookalike(req: OperationRequest, clientGroups?: Map<string, string>): boolean {
  return (
    req.duplicateGroupId !== null ||
    req.duplicateOperationId !== null ||
    clientGroups?.has(req.id) === true
  );
}

/** Ids of every look-alike in the list — what «Утвердить все» must warn about. */
export function lookalikeIds(requests: OperationRequest[]): string[] {
  const groups = clientDuplicateGroups(requests);
  return requests.filter((r) => isLookalike(r, groups)).map((r) => r.id);
}

/** The 409 body the server sends when a submit looks like a repeat, or null for
 * any other error (so the caller falls through to its normal error toast). */
export function duplicateSuspectedPayload(error: unknown): DuplicateSuspectedPayload | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return null;
  const data = error.response.data as Partial<DuplicateSuspectedPayload> | undefined;
  if (data?.code !== 'DUPLICATE_SUSPECTED' || !Array.isArray(data.duplicates)) return null;
  return { code: 'DUPLICATE_SUSPECTED', duplicates: data.duplicates };
}
